import * as XLSX from "xlsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Tag, message, Input } from "antd";
import * as api from "../../../utils/api";
import { PageLayout } from "../../../components/page-layout";
import { CreateCampaignModal } from "../../../components/create-campaing-modal";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import * as S from "./styles";

const { Search } = Input;

const DEFAULT_ERROR_MESSAGE =
  "Erro inesperado. Por favor, tente novamente mais tarde.";

export const CampaignsPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaings, setCampaigns] = useState([]);
  const [campaignsDisplay, setCampaignsDisplay] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const getIsActiveTag = (isActive) => {
    if (!isActive) {
      return <Tag color="grey">INATIVA</Tag>;
    } else {
      return <Tag color="green">ATIVA</Tag>;
    }
  };

  const columns = [
    {
      title: "Título",
      key: "title",
      dataIndex: "title",
    },
    {
      title: "Status",
      key: "is_active",
      render: (_, { is_active }) => getIsActiveTag(is_active),
    },
    {
      title: "Ações",
      key: "action",
      render: (_, { id, is_active, banner_file_key, banner_mobile_file_key }) => (
        <S.ActionsColumnContainer>
          <span onClick={() => handleToggleCampaignStatus(id)}>
            {is_active ? "Desativar" : "Ativar"}
          </span>
          <span
            onClick={() =>
              navigate(`/campanhas/${id}`, {
                state: {
                  banner_file_key,
                  banner_mobile_file_key
                },
              })
            }
          >
            Gerenciar Questionário
          </span>
          <span onClick={() => handleExportXls(id)}>Exportar Respostas</span>
          <span
            onClick={() => {
              setSelectedCampaign(campaings.find((i) => i.id === id));
              setOpenRemove(true);
            }}
          >
            Remover
          </span>
        </S.ActionsColumnContainer>
      ),
    },
  ];

  const handleGetCampaigns = async () => {
    setLoading(true);

    try {
      const { result, error } = await api.post("/get-campaigns");
      console.log("Result:", result);

      if (error) {
        throw new Error(error);
      }

      const _result = result ? (Array.isArray(result) ? result : [result]) : [];

      const mapped = _result?.map((i, index) => ({ ...i, key: index })) || [];

      console.log("Mapped campaigns:", mapped);

      setCampaigns(mapped);
      setCampaignsDisplay(mapped);
    } catch (err) {
      message.error(err.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCampaignStatus = async (campaignId) => {
    try {
      const { error } = await api.post("/toggle-campaign", {
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      message.success("Status da campanha alterado com sucesso.");
      setSelectedCampaign(null);

      await handleGetCampaigns();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleCreateCampaign = async (payload) => {
    try {
      const { error } = await api.post("/create-campaign", payload);

      if (error) {
        throw new Error(error);
      }

      setOpenCreate(false);
      await handleGetCampaigns();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleRemoveCampaign = async (e) => {
    e.preventDefault();

    try {
      const { error } = await api.post("/remove-campaign", {
        campaignId: selectedCampaign.id,
      });

      if (error) {
        throw new Error(error);
      }

      setOpenRemove(false);
      await handleGetCampaigns();
    } catch (error) {
      message.error(error.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setSelectedCampaign(null);
    }
  };

  const handleExportXls = async (campaignId) => {
    try {
      const { result, error } = await api.post("/export-answers", {
        campaignId,
      });

      if (error) {
        throw new Error(error);
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(result);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success("Exportação realizada com sucesso.");
    } catch (err) {
      message.error(err.message || DEFAULT_ERROR_MESSAGE);
    }
  };

  const onSearch = async (value, _e, info) => {
    if (info.source === "clear") {
      handleGetCampaigns();
      return null;
    }

    const filteredCampaigns = campaings.filter((campaign) => {
      return campaign.title.toLowerCase().includes(value.toLowerCase());
    });

    setCampaignsDisplay(filteredCampaigns);
  };

  useEffect(() => {
    handleGetCampaigns();
  }, []);

  return (
    <>
      <PageLayout>
        <S.Container>
          <div id="search-container">
            <Search
              placeholder="Buscar campanha"
              allowClear
              onSearch={onSearch}
            />
          </div>
          <div id="action-container">
            <Button
              type="primary"
              onClick={() => setOpenCreate(true)}
              id="app-button"
            >
              Registrar
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={campaignsDisplay}
            size="small"
            bordered={true}
            loading={loading}
            pagination={{
              defaultPageSize: 10,
            }}
          />
        </S.Container>
      </PageLayout>
      <CreateCampaignModal
        open={openCreate}
        onCancel={() => setOpenCreate(false)}
        onSubmit={handleCreateCampaign}
      />
      <ConfirmationModal
        open={openRemove}
        onCancel={() => {
          setSelectedCampaign(null);
          setOpenRemove(false);
        }}
        onOk={handleRemoveCampaign}
      />
    </>
  );
};
